import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ElimReclassLocalService {

  private headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

  constructor(private http: HttpClient) {}

  getCodeCombination(baseUrl: string, combinationKey: number): Observable<any> {
    const urlParams = new URLSearchParams();
    urlParams.set('action_id', '9');
    urlParams.set('combination_key', combinationKey.toString());

    return this.http.post<any>(
      baseUrl + '/loadJsonObject',
      urlParams.toString(),
      { headers: this.headers, withCredentials: true }
    ).pipe(
      map(response => {
        if (response.errorMessage && response.errorMessage !== 'null') {
          throw new Error(response.errorMessage);
        }
        return response.jsonObject?.[0] || null;
      })
    );
  }

  private buildParams(params: Record<string, any>, globalService?: any): URLSearchParams {
    const urlParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlParams.set(key, params[key].toString());
      }
    });
    if (globalService?.globalParams) {
      Object.keys(globalService.globalParams).forEach(key => {
        if (!urlParams.has(key)) {
          urlParams.set(key, globalService.globalParams[key]?.toString() || '');
        }
      });
    }
    return urlParams;
  }

  private loadJson(baseUrl: string, params: Record<string, any>, globalService?: any): Observable<any[]> {
    const urlParams = this.buildParams(params, globalService);
    return this.http.post<any>(
      baseUrl + '/loadJsonObject',
      urlParams.toString(),
      { headers: this.headers, withCredentials: true }
    ).pipe(
      map(response => {
        if (response.errorMessage && response.errorMessage !== 'null') {
          throw new Error(response.errorMessage);
        }
        return response.jsonObject || [];
      })
    );
  }

  /** action_code: nsh8fs - Load feed details for HO */
  getFeeds(baseUrl: string, taxYear: string, scenario: string, hoCombinationKey: number, globalService: any): Observable<any[]> {
    return this.loadJson(baseUrl, {
      action_code: 'nsh8fs',
      tax_year: taxYear,
      scenario: scenario,
      ho_combination_key: hoCombinationKey
    }, globalService);
  }

  /** action_code: wi4d93 - Get pre/post elim amounts */
  getAmounts(baseUrl: string, combinationKey: number, hoCombinationKey: number, acctKey: number, tpKey: number, globalService: any): Observable<any[]> {
    return this.loadJson(baseUrl, {
      action_code: 'wi4d93',
      combination_key: combinationKey,
      ho_combination_key: hoCombinationKey,
      acct_key: acctKey,
      tp_key: tpKey
    }, globalService);
  }

  /** action_code: 98ec1q - Search accounts for elimination */
  getAccounts(baseUrl: string, screenKey: number, combinationKey: string | number | null, chartKey: number | null, searchString: string, acctTypeStr: string, sourceType: string, globalService: any): Observable<any[]> {
    return this.loadJson(baseUrl, {
      action_code: '98ec1q',
      screen_key: screenKey,
      combination_key: combinationKey,
      source_type: sourceType,
      chart_key: chartKey,
      search_type: 'ACCT',
      search_string: searchString,
      acct_type_str: acctTypeStr
    }, globalService);
  }

  /** action_code: 50swfi - Search trading partners */
  getTradingPartners(baseUrl: string, taxYear: string, scenario: string, searchString: string, globalService: any): Observable<any[]> {
    return this.loadJson(baseUrl, {
      action_code: '50swfi',
      tax_year: taxYear,
      scenario: scenario,
      search_string: searchString
    }, globalService);
  }

  /** action_code: mk7ss9 - Load existing eliminations */
  loadExistingElims(baseUrl: string, screenKey: number, chartKey: number, hoCombinationKey: number, globalService: any): Observable<any[]> {
    return this.loadJson(baseUrl, {
      action_code: 'mk7ss9',
      screen_key: screenKey,
      chart_key: chartKey,
      ho_combination_key: hoCombinationKey
    }, globalService);
  }

  /** action_id: 108 - Save/update eliminations */
  saveUpdateEliminations(baseUrl: string, settings: any[], accounts: any[], globalService: any, filterParams: any): Observable<any> {
    let params: any = { ...filterParams };
    if (globalService?.globalParams) {
      params = { ...globalService.globalParams, ...params };
    }
    params.jsonObj = JSON.stringify(accounts);
    params.jsonSettings = JSON.stringify(settings);
    params.process_name = 'ELIMINATION_SAVE_LOCAL';

    const urlParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlParams.set(key, params[key].toString());
      }
    });

    return this.http.post<any>(
      baseUrl + '/saveJsonObject?action_id=108',
      urlParams.toString(),
      { headers: this.headers, withCredentials: true }
    );
  }

  /** action_id: 109 - Delete eliminations */
  deleteElims(baseUrl: string, deleteData: any, globalService: any, filterParams: any): Observable<any> {
    let params: any = { ...filterParams };
    if (globalService?.globalParams) {
      params = { ...globalService.globalParams, ...params };
    }
    params = { ...deleteData, ...params };

    const urlParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlParams.set(key, params[key].toString());
      }
    });

    return this.http.post<any>(
      baseUrl + '/saveJsonObject?action_id=109',
      urlParams.toString(),
      { headers: this.headers, withCredentials: true }
    );
  }
}
